import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import { createContext, useContext, type ReactNode, createElement, useMemo } from "react";
import { dealApi, type DealFeedRequest } from "~/api/deal";
import type { DealClientListData } from "~/types/deal";
import { apiErrorMessage, reportUnexpectedError } from "~/api/client";

export interface RegularDeal extends DealClientListData {}

export interface RegularDealsState {
  deals: RegularDeal[];
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  totalDeals: number;
  pageSize: number;
  entryPoint: string;
  hasMore: boolean;
  fetchDeals: (entryPoint: string, page?: number, channel?: string) => Promise<void>;
  fetchMoreDeals: (channel?: string) => Promise<void>;
  resetDeals: () => void;
  // Atomic operation to check and set loading state
  trySetLoading: () => boolean;
  setNotLoading: () => void;
}

const createRegularDealsStore = () => {
  return createStore<RegularDealsState>((set, get) => ({
    deals: [],
    isLoading: false,
    error: null,
    currentPage: 1,
    totalDeals: 0,
    pageSize: 20,
    entryPoint: "",
    hasMore: true,

    // Atomic check-and-set operation for loading state
    trySetLoading: () => {
      const state = get();
      if (state.isLoading) {
        return false; // Already loading, operation rejected
      }

      set(state => ({
        ...state,
        isLoading: true,
        error: null,
      }));
      return true; // Successfully set to loading
    },

    setNotLoading: () => {
      set(state => ({
        ...state,
        isLoading: false,
      }));
    },

    fetchDeals: async (entryPoint: string, page = 1, channel?: string) => {
      // Use atomic check-and-set
      if (!get().trySetLoading()) {
        return;
      }

      try {
        const state = get();
        const request: DealFeedRequest = {
          entryPoint,
          page,
          pageSize: page === 1 ? 100 : state.pageSize,
          channel,
        };

        const response = await dealApi.getDealFeed(request);
        const newDeals = response.deals as RegularDeal[];

        // Limit total deals to 500 by removing the oldest items first
        set(state => {
          const mergedDeals = page === 1 ? newDeals : [...state.deals, ...newDeals];
          const cappedDeals = mergedDeals.length > 500 ? mergedDeals.slice(mergedDeals.length - 500) : mergedDeals;

          return {
            ...state,
            deals: cappedDeals,
            currentPage: page,
            totalDeals: response.total ?? 0,
            entryPoint,
            hasMore: newDeals.length > 0,
          };
        });
      } catch (error) {
        reportUnexpectedError(error);
        set(state => ({
          ...state,
          error: apiErrorMessage(error, "Failed to fetch deals"),
        }));
      } finally {
        get().setNotLoading();
      }
    },

    fetchMoreDeals: async (channel?: string) => {
      const state = get();
      if (state.isLoading || !state.hasMore) return;

      await get().fetchDeals(state.entryPoint, state.currentPage + 1, channel);
    },

    resetDeals: () => {
      set({
        deals: [],
        isLoading: false,
        error: null,
        currentPage: 1,
        totalDeals: 0,
        pageSize: 20,
        entryPoint: "",
        hasMore: true,
      });
    },
  }));
};

// Global store registry for persistent stores
const globalStoreRegistry = new Map<string, ReturnType<typeof createRegularDealsStore>>();

// Function to get or create a persistent store instance
export const getRegularDealsStore = (channelId: string) => {
  if (!globalStoreRegistry.has(channelId)) {
    const store = createRegularDealsStore();
    globalStoreRegistry.set(channelId, store);
  }
  return globalStoreRegistry.get(channelId)!;
};

// Function to manually cleanup a store (optional, for memory management)
export const cleanupRegularDealsStore = (channelId: string) => {
  globalStoreRegistry.delete(channelId);
};

// Function to get all active store channels (for debugging)
export const getActiveStoreChannels = () => {
  return Array.from(globalStoreRegistry.keys());
};

// Create React Context for the current store
const RegularDealsStoreContext = createContext<ReturnType<typeof createRegularDealsStore> | null>(null);

// Provider component that provides persistent store instances
export interface RegularDealsStoreProviderProps {
  children: ReactNode;
  channelId: string;
}

export const RegularDealsStoreProvider = ({ children, channelId }: RegularDealsStoreProviderProps) => {
  // Get persistent store instance (creates once, reuses forever)
  const store = useMemo(() => getRegularDealsStore(channelId), [channelId]);

  return createElement(RegularDealsStoreContext.Provider, { value: store }, children);
};

// Hook to use the store from context
export const useRegularDealsStore = () => {
  const store = useContext(RegularDealsStoreContext);
  if (!store) {
    throw new Error("useRegularDealsStore must be used within a RegularDealsStoreProvider");
  }
  return useStore(store);
};

// Direct hook to use store without context (alternative approach)
export const useRegularDealsStoreByChannelId = (channelId: string) => {
  const store = useMemo(() => getRegularDealsStore(channelId), [channelId]);
  return useStore(store);
};
